import React, { useEffect, useRef, useState } from 'react';

export const CROP_VIEWPORT = 240;
export const MIN_CROP_ZOOM = 1;
export const MAX_CROP_ZOOM = 4;

const OUTPUT_SIZE = 1024;
const TARGET_AVATAR_BYTES = Math.floor(1.85 * 1024 * 1024);
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

export type AvatarCropValue = {
  offsetX: number;
  offsetY: number;
  zoom: number;
};

function coverScale(imgW: number, imgH: number, view: number, zoom: number): number {
  return Math.max(view / imgW, view / imgH) * zoom;
}

export function clampCropOffset(
  offsetX: number,
  offsetY: number,
  imgW: number,
  imgH: number,
  zoom: number,
  view = CROP_VIEWPORT,
): AvatarCropValue {
  const scale = coverScale(imgW, imgH, view, zoom);
  const displayW = imgW * scale;
  const displayH = imgH * scale;
  const maxX = Math.max(0, (displayW - view) / 2);
  const maxY = Math.max(0, (displayH - view) / 2);
  return {
    offsetX: Math.min(maxX, Math.max(-maxX, offsetX)),
    offsetY: Math.min(maxY, Math.max(-maxY, offsetY)),
    zoom,
  };
}

async function blobFromSource(src: Blob | string): Promise<Blob> {
  if (src instanceof Blob) return src;
  const res = await fetch(src);
  if (!res.ok) throw new Error('Could not load image for crop.');
  return res.blob();
}

async function bitmapFromBlob(blob: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(blob, { imageOrientation: 'from-image' } as ImageBitmapOptions);
  } catch {
    return createImageBitmap(blob);
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('Failed to read cropped image'));
    reader.readAsDataURL(blob);
  });
}

function encodeJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Image encode failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export async function bakeAvatarCrop(
  src: Blob | string,
  crop: AvatarCropValue,
): Promise<{ dataUrl: string; bytes: number }> {
  const blob = await blobFromSource(src);
  const bitmap = await bitmapFromBlob(blob);
  const imgW = bitmap.width;
  const imgH = bitmap.height;
  if (!imgW || !imgH) {
    bitmap.close();
    throw new Error('Image has no dimensions.');
  }

  const clamped = clampCropOffset(crop.offsetX, crop.offsetY, imgW, imgH, crop.zoom);
  const scale = coverScale(imgW, imgH, CROP_VIEWPORT, clamped.zoom);
  const left = (CROP_VIEWPORT - imgW * scale) / 2 + clamped.offsetX;
  const top = (CROP_VIEWPORT - imgH * scale) / 2 + clamped.offsetY;
  const srcSize = CROP_VIEWPORT / scale;
  const srcX = Math.max(0, -left / scale);
  const srcY = Math.max(0, -top / scale);
  const srcW = Math.min(srcSize, imgW - srcX);
  const srcH = Math.min(srcSize, imgH - srcY);

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Canvas unavailable');
  }
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  ctx.drawImage(bitmap, srcX, srcY, srcW, srcH, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  bitmap.close();

  const qualities = [0.86, 0.74, 0.62, 0.5, 0.38];
  let lastBlob: Blob | null = null;
  for (const quality of qualities) {
    const encoded = await encodeJpeg(canvas, quality);
    lastBlob = encoded;
    if (encoded.size <= TARGET_AVATAR_BYTES) {
      const dataUrl = await blobToDataUrl(encoded);
      if (!dataUrl.startsWith('data:image/jpeg;base64,/9j/')) {
        throw new Error('Crop produced an invalid JPEG. Try another photo.');
      }
      return { dataUrl, bytes: encoded.size };
    }
  }
  if (!lastBlob || lastBlob.size > MAX_AVATAR_BYTES) {
    throw new Error('Cropped photo is still over 2 MB. Try zooming in.');
  }
  return { dataUrl: await blobToDataUrl(lastBlob), bytes: lastBlob.size };
}

function touchDistance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

type AvatarCropperProps = {
  src: string;
  value: AvatarCropValue;
  onChange: (next: AvatarCropValue) => void;
  disabled?: boolean;
};

export const AvatarCropper: React.FC<AvatarCropperProps> = ({
  src,
  value,
  onChange,
  disabled = false,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<{
    type: 'pan' | 'pinch';
    originX: number;
    originY: number;
    originZoom: number;
    startX: number;
    startY: number;
    startDist: number;
  } | null>(null);
  const valueRef = useRef(value);
  const imgSizeRef = useRef<{ w: number; h: number } | null>(null);
  const onChangeRef = useRef(onChange);
  const disabledRef = useRef(disabled);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const [loadError, setLoadError] = useState('');

  valueRef.current = value;
  imgSizeRef.current = imgSize;
  onChangeRef.current = onChange;
  disabledRef.current = disabled;

  useEffect(() => {
    setImgSize(null);
    setLoadError('');
    pointersRef.current.clear();
    gestureRef.current = null;
  }, [src]);

  const emit = (offsetX: number, offsetY: number, zoom: number) => {
    const size = imgSizeRef.current;
    const nextZoom = Math.min(MAX_CROP_ZOOM, Math.max(MIN_CROP_ZOOM, zoom));
    if (!size) {
      onChangeRef.current({ offsetX, offsetY, zoom: nextZoom });
      return;
    }
    onChangeRef.current(clampCropOffset(offsetX, offsetY, size.w, size.h, nextZoom));
  };

  const beginGesture = () => {
    const pts = [...pointersRef.current.values()];
    const current = valueRef.current;
    if (pts.length >= 2) {
      gestureRef.current = {
        type: 'pinch',
        originX: current.offsetX,
        originY: current.offsetY,
        originZoom: current.zoom,
        startX: 0,
        startY: 0,
        startDist: Math.max(8, touchDistance(pts[0], pts[1])),
      };
      return;
    }
    if (pts.length === 1) {
      gestureRef.current = {
        type: 'pan',
        originX: current.offsetX,
        originY: current.offsetY,
        originZoom: current.zoom,
        startX: pts[0].x,
        startY: pts[0].y,
        startDist: 0,
      };
      return;
    }
    gestureRef.current = null;
  };

  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;

    const onPointerDown = (e: PointerEvent) => {
      if (disabledRef.current) return;
      e.preventDefault();
      if (e.pointerType !== 'touch') {
        node.setPointerCapture(e.pointerId);
      }
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      beginGesture();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      e.preventDefault();
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (gesture.type === 'pinch') {
        const pts = [...pointersRef.current.values()];
        if (pts.length < 2) return;
        const ratio = touchDistance(pts[0], pts[1]) / gesture.startDist;
        emit(gesture.originX, gesture.originY, gesture.originZoom * ratio);
        return;
      }

      const point = pointersRef.current.get(e.pointerId);
      if (!point) return;
      let zoom = gesture.originZoom;
      const size = imgSizeRef.current;
      if (size) {
        const slack = Math.max(
          0,
          (size.w * coverScale(size.w, size.h, CROP_VIEWPORT, zoom) - CROP_VIEWPORT) / 2,
        );
        if (slack < 1) {
          zoom = Math.max(zoom, 1.25);
        }
      }
      emit(
        gesture.originX + (point.x - gesture.startX),
        gesture.originY + (point.y - gesture.startY),
        zoom,
      );
    };

    const onPointerUp = (e: PointerEvent) => {
      pointersRef.current.delete(e.pointerId);
      if (node.hasPointerCapture?.(e.pointerId)) {
        node.releasePointerCapture(e.pointerId);
      }
      beginGesture();
    };

    const onWheel = (e: WheelEvent) => {
      if (disabledRef.current) return;
      e.preventDefault();
      const current = valueRef.current;
      const nextZoom = current.zoom * (e.deltaY < 0 ? 1.08 : 0.92);
      emit(current.offsetX, current.offsetY, nextZoom);
    };

    node.addEventListener('pointerdown', onPointerDown, { passive: false });
    node.addEventListener('pointermove', onPointerMove, { passive: false });
    node.addEventListener('pointerup', onPointerUp);
    node.addEventListener('pointercancel', onPointerUp);
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerUp);
      node.removeEventListener('wheel', onWheel);
    };
  }, []);

  const scale = imgSize
    ? coverScale(imgSize.w, imgSize.h, CROP_VIEWPORT, value.zoom)
    : 1;
  const displayW = imgSize ? imgSize.w * scale : CROP_VIEWPORT;
  const displayH = imgSize ? imgSize.h * scale : CROP_VIEWPORT;

  return (
    <div className="w-full">
      <div
        ref={viewportRef}
        className={`relative mx-auto overflow-hidden rounded-full bg-stone-200 border-2 border-white shadow-sm ${
          disabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
        }`}
        style={{ width: CROP_VIEWPORT, height: CROP_VIEWPORT, touchAction: 'none' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <img
          src={src}
          alt="Avatar crop"
          draggable={false}
          className="absolute max-w-none select-none pointer-events-none"
          style={
            imgSize
              ? {
                  width: displayW,
                  height: displayH,
                  left: (CROP_VIEWPORT - displayW) / 2 + value.offsetX,
                  top: (CROP_VIEWPORT - displayH) / 2 + value.offsetY,
                }
              : {
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }
          }
          onLoad={(e) => {
            const el = e.currentTarget;
            const w = el.naturalWidth;
            const h = el.naturalHeight;
            if (w > 0 && h > 0) setImgSize({ w, h });
          }}
          onError={() => setLoadError('Could not preview that photo. Try JPEG or PNG.')}
        />
      </div>
      {loadError ? (
        <p className="mt-2 text-xs text-red-700 text-center">{loadError}</p>
      ) : (
        <>
          <label className="mt-2 flex items-center gap-2 text-xs text-stone-600">
            <span className="shrink-0">Zoom</span>
            <input
              type="range"
              min={MIN_CROP_ZOOM}
              max={MAX_CROP_ZOOM}
              step={0.01}
              value={value.zoom}
              disabled={disabled}
              onChange={(e) => emit(value.offsetX, value.offsetY, Number(e.target.value))}
              className="flex-1 accent-stone-900"
            />
          </label>
          <p className="mt-1 text-xs text-stone-500 text-center">
            Drag to move. Pinch or use zoom if it feels stuck.
          </p>
        </>
      )}
    </div>
  );
};
